/* ============================================================
   JUCE 3.0 · theme-gift.js - the gift card composer.

   One product on the whole site renders [data-giftcard] (template suffix
   giftcard), so the composer is imported only there, in the critical tier,
   and modulepreloaded by the layout on that template.

   HOW IT REACHES THEME.JS. The code below is theme.js's own, moved as it
   stood. What it still uses from theme.js arrives through mount(): the
   `let` list is filled once, from the kit theme.js hands over (chunkKit
   there), before the composer runs.
   Nothing here imports theme.js by URL: a URL without the ?v= digest
   would be a second copy of theme.js, booted twice.
   ============================================================ */
let bagMoney;

export function mount(k) {
  ({
    bagMoney
  } = k);
  return { initGiftCard };
}

/* ============================================================
   THE GIFT CARD COMPOSER  (/products/gift-card)

   Three jobs, and only the third one is load-bearing:

     1. paint the card as it is written
     2. say back, in a sentence, who gets it and when
     3. assemble the properties the Vify app reads off the order

   (3) IS AN EXTERNAL INTERFACE. Vify has no backend handshake at add-to-cart
   time - its own bundle posts to /cart/add.js with a properties object and
   its server reads that object off the order webhook afterwards to generate
   the code and send the email. So these key names and this JSON shape are not
   ours to tidy: they are what the app was reading when every gift card this
   store has ever sold went through it. snippets/gift-card.liquid carries the
   full field list and where it was verified.

   WHY THE SUBMIT LISTENER IS BOUND ON THE FORM. initCart listens on
   `document` and builds its FormData there, so anything that has to be IN
   that FormData must be written before the event reaches document. A listener
   on the form itself runs in the target phase, which is strictly earlier than
   any bubble-phase listener above it. Move this to document and the payload
   silently stops shipping - the add still succeeds, the card just never
   arrives.

   Native validation gates all of it: required + type="email" means the submit
   event does not fire at all on an incomplete form, so there is no path where
   the properties are half-written.
   ============================================================ */
function initGiftCard() {
  const root = document.querySelector('[data-giftcard]');
  if (!root) return;
  const form = root.querySelector('[data-gift-form]');
  if (!form) return;

  const q = (sel) => root.querySelector(sel);
  const toIn = q('[data-gc-to]');
  const emailIn = q('[data-gc-email]');
  const fromIn = q('[data-gc-from]');
  const msgIn = q('[data-gc-message]');
  const dateIn = q('[data-gc-date]');
  const timeIn = q('[data-gc-time]');
  const whenFields = q('[data-gc-when-fields]');
  const emailLabel = q('[data-gc-email-label]');
  const emailMsg = q('[data-gc-msg-email]');
  const count = q('[data-gc-count]');
  const review = q('[data-gc-review]');
  const sendLabel = q('[data-gc-send-label]');
  const payload = q('[data-gc-payload]');
  const imageIn = q('[data-gc-image]');
  const art = q('[data-gc-art]');
  const pTo = q('[data-gc-p-to]');
  const pFrom = q('[data-gc-p-from]');
  const pMsg = q('[data-gc-p-message]');
  const pAmt = q('[data-gc-p-amount]');
  const card = q('[data-gc-card]');
  const modal = q('[data-gc-preview]');
  if (!toIn || !emailIn || !fromIn || !msgIn) return;

  const PRODUCT_ID = parseInt(root.dataset.gcProduct, 10) || 0;
  const MAX = parseInt(root.dataset.gcMax, 10) || 500;
  const EMAIL_HELP = {
    them: 'The card is emailed here. Check it twice.',
    me: 'The card comes to you with their name on it, so you can hand it over yourself.'
  };

  /* A mistyped IANA name in the theme editor throws RangeError out of every
     Intl call below, which would take the whole controller down and leave a
     dead Add button. Resolved once, here, and never trusted again. */
  let ZONE = root.dataset.gcTz || 'Africa/Johannesburg';
  try { new Intl.DateTimeFormat('en-GB', { timeZone: ZONE }); }
  catch (e) {
    try { new Intl.DateTimeFormat('en-GB', { timeZone: 'Africa/Johannesburg' }); ZONE = 'Africa/Johannesburg'; }
    catch (e2) { ZONE = undefined; }
  }

  /* ---- the store's wall clock ----
     Everything scheduled is expressed in the SHOP's timezone, not the
     buyer's: "08:00" on a card bought from London still means 08:00 here.
     That also makes the date picker's own floor correct - a shopper whose
     local day has not started yet must still be able to choose today. */
  const zoneParts = (date) => {
    const out = {};
    try {
      new Intl.DateTimeFormat('en-GB', {
        timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(date).forEach((p) => { if (p.type !== 'literal') out[p.type] = p.value; });
    } catch (e) { /* falls through to the local-clock defaults below */ }
    if (!out.year) {
      const pad = (n) => String(n).padStart(2, '0');
      out.year = String(date.getFullYear()); out.month = pad(date.getMonth() + 1);
      out.day = pad(date.getDate()); out.hour = pad(date.getHours()); out.minute = pad(date.getMinutes());
    }
    /* hour12:false yields "24" for midnight in some engines. */
    if (out.hour === '24') out.hour = '00';
    return out;
  };
  const zoneDate = (date) => { const p = zoneParts(date); return p.year + '-' + p.month + '-' + p.day; };
  const zoneStamp = (date) => { const p = zoneParts(date); return zoneDate(date) + 'T' + p.hour + ':' + p.minute; };
  const zoneShort = () => {
    try {
      const p = new Intl.DateTimeFormat('en-ZA', { timeZone: ZONE, timeZoneName: 'short' })
        .formatToParts(new Date()).filter((x) => x.type === 'timeZoneName')[0];
      return p ? p.value : '';
    } catch (e) { return ''; }
  };
  /* Read back as a date, never as the ISO string the input holds: "2026-09-14"
     is not a sentence. Formatted in UTC off a UTC-built date so the day cannot
     slip by one on either side of the world. */
  const humanDate = (ymd, hm) => {
    const bits = String(ymd || '').split('-');
    if (bits.length !== 3) return '';
    const dt = new Date(Date.UTC(+bits[0], +bits[1] - 1, +bits[2]));
    let out;
    try {
      out = new Intl.DateTimeFormat('en-ZA', {
        timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric'
      }).format(dt);
    } catch (e) { out = ymd; }
    return hm ? out + ' at ' + hm : out;
  };

  const modeNow = () => {
    const el = root.querySelector('[data-gc-mode]:checked');
    return el ? el.dataset.gcMode : 'them';
  };
  const whenNow = () => {
    const el = root.querySelector('[data-gc-when]:checked');
    return el ? el.dataset.gcWhen : 'now';
  };

  /* ---- 1 · the card ---- */
  function paint() {
    const to = toIn.value.trim();
    const from = fromIn.value.trim();
    const msg = msgIn.value.trim();
    if (pTo) pTo.textContent = to || 'someone lovely';
    if (pFrom) pFrom.textContent = from || 'you';
    if (pMsg) {
      pMsg.textContent = msg || 'Your message will appear here, in your words.';
      pMsg.classList.toggle('is-placeholder', !msg);
    }
    const amt = form.querySelector('input[name="id"]:checked');
    if (pAmt && amt && amt.dataset.priceLabel) pAmt.textContent = amt.dataset.priceLabel;
    const design = root.querySelector('[data-gc-design]:checked');
    if (art && design && design.value && art.getAttribute('src') !== design.value) {
      art.src = design.value;
    }
  }

  function paintCount() {
    if (!count) return;
    const n = msgIn.value.length;
    count.textContent = n + ' / ' + MAX;
    count.classList.toggle('is-near', n > MAX - 40);
  }

  /* ---- who it goes to ----
     The buyer's own address is offered when they switch to "send it to me",
     and withdrawn when they switch back - but only while it is still OURS.
     The moment they type in that field it is theirs and neither switch may
     touch it again. */
  let autoMine = false;
  function paintMode() {
    const me = modeNow() === 'me';
    if (emailLabel) emailLabel.textContent = me ? 'Your email' : 'Their email';
    if (emailMsg && !emailMsg.dataset.gcSuggesting) emailMsg.textContent = me ? EMAIL_HELP.me : EMAIL_HELP.them;
    const mine = emailIn.dataset.gcMine || '';
    if (me && mine && (!emailIn.value || autoMine)) { emailIn.value = mine; autoMine = true; }
    else if (!me && autoMine) { emailIn.value = ''; autoMine = false; }
  }

  function paintWhen() {
    const later = whenNow() === 'later';
    if (whenFields) whenFields.hidden = !later;
    if (dateIn) {
      const floor = zoneDate(new Date());
      dateIn.min = floor;
      /* Two years is not a policy, it is a typo guard: a mis-keyed year is
         the one way to schedule a card into a decade nobody is waiting in. */
      dateIn.max = (parseInt(floor.slice(0, 4), 10) + 2) + floor.slice(4);
      dateIn.required = later;
      if (later && !dateIn.value) dateIn.value = floor;
    }
    if (timeIn) timeIn.required = later;
  }

  /* ---- 2 · the sentence ----
     Built out of nodes rather than a string of HTML: two of the three things
     in it are typed by the shopper, and the one place on this page where
     their text is re-displayed with emphasis is not the place to start
     concatenating markup. */
  function paintReview() {
    if (!review) return;
    review.textContent = '';
    review.classList.remove('is-warn');
    const me = modeNow() === 'me';
    const later = whenNow() === 'later';
    const name = toIn.value.trim();
    const email = emailIn.value.trim();
    const add = (t) => review.appendChild(document.createTextNode(t));
    const strong = (t) => {
      const s = document.createElement('strong');
      s.textContent = t;
      review.appendChild(s);
    };
    /* ---- IT NAMES WHAT IS MISSING (owner, 2026-08-28) ----
       "Element gc-review to say what is still required if empty."

       It used to say one sentence for every unfinished state: "Fill in where
       it should go and we will say back exactly what happens." That is true
       and it is useless - it does not say WHICH box, and this form has four
       required fields spread over four steps, two of which only exist once
       "on a chosen day" is picked. A shopper who pressed Add and got a native
       validation bubble on a field two screens up had been told nothing by the
       line that exists to tell them.

       So the review reads the required fields itself and lists the ones still
       empty, in the order they appear on the page. The list is BUILT from the
       controls rather than typed, so a field that becomes required later (the
       date and time, the moment "later" is chosen) is covered without a second
       edit here - which is exactly the pair that used to be missed.

       is-warn, the same class the email typo guard uses, because this is the
       page telling somebody something is wrong rather than reading their
       choices back. */
    const wanted = [
      [toIn, 'who it is for'],
      [emailIn, me ? 'your email address' : 'their email address'],
      [fromIn, 'who it is from'],
    ];
    if (later) {
      wanted.push([dateIn, 'the day it should arrive']);
      wanted.push([timeIn, 'the time of day']);
    }
    const missing = wanted
      .filter((pair) => pair[0] && !String(pair[0].value || '').trim())
      .map((pair) => pair[1]);
    if (missing.length) {
      review.classList.add('is-warn');
      add('Still to fill in: ');
      /* Named with an "and" rather than a comma list, because three items with
         no conjunction reads as a heading. */
      missing.forEach((what, i) => {
        if (i) add(i === missing.length - 1 ? ' and ' : ', ');
        strong(what);
      });
      add('.');
      return;
    }
    if (me) { add('This lands in your inbox at '); strong(email); }
    /* Named, it is a sentence about a person. Unnamed, it has to become a
       sentence about the card instead - "They gets this" is what falling back
       to a pronoun in the subject slot actually produces. */
    else if (name) { strong(name); add(' gets this at '); strong(email); }
    else { add('This goes to '); strong(email); }
    if (later && dateIn && dateIn.value) {
      add(' on ');
      strong(humanDate(dateIn.value, timeIn ? timeIn.value : ''));
      const z = zoneShort();
      if (z) add(' ' + z);
    } else {
      add(' as soon as your order goes through');
    }
    add(me ? ', for you to pass on.' : '.');
  }

  /* ---- the typo guard ----
     A wrong size is an exchange. A wrong email address is money handed to a
     stranger, silently, with no bounce and no way back - it is the single
     most expensive mistake available on this page, so it gets the one piece
     of cleverness on it. Suggestion only, never a correction: the shopper
     may well know an address we have never seen. */
  const DOMAINS = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
    'live.com', 'me.com', 'webmail.co.za', 'telkomsa.net', 'mweb.co.za',
    'vodamail.co.za', 'gmail.co.za', 'yahoo.co.za'
  ];
  /* Damerau, not plain Levenshtein, and the difference is the whole feature:
     an adjacent TRANSPOSITION is the commonest way an address is mistyped
     ("gmial", "hotmial", "yahho") and plain Levenshtein scores it 2, the same
     as two unrelated wrong letters. At a threshold of 1 that means the single
     most likely typo on the page is the one case that never gets caught. */
  function editDistance(a, b) {
    const m = a.length; const n = b.length;
    const d = [];
    for (let i = 0; i <= m; i++) { d[i] = [i]; }
    for (let j = 0; j <= n; j++) { d[0][j] = j; }
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 &&
            a.charAt(i - 1) === b.charAt(j - 2) &&
            a.charAt(i - 2) === b.charAt(j - 1)) {
          d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
        }
      }
    }
    return d[m][n];
  }
  function suggestFor(value) {
    const at = value.lastIndexOf('@');
    if (at < 1) return '';
    const domain = value.slice(at + 1).toLowerCase();
    if (!domain || DOMAINS.indexOf(domain) !== -1) return '';
    for (let i = 0; i < DOMAINS.length; i++) {
      const target = DOMAINS[i];
      if (Math.abs(target.length - domain.length) > 2) continue;
      if (editDistance(domain, target) <= (target.length > 10 ? 2 : 1)) {
        return value.slice(0, at + 1) + target;
      }
    }
    return '';
  }
  function checkEmail() {
    if (!emailMsg) return;
    const guess = suggestFor(emailIn.value.trim());
    if (!guess) {
      delete emailMsg.dataset.gcSuggesting;
      emailMsg.textContent = modeNow() === 'me' ? EMAIL_HELP.me : EMAIL_HELP.them;
      return;
    }
    emailMsg.dataset.gcSuggesting = '1';
    emailMsg.textContent = 'Did you mean ';
    const fix = document.createElement('button');
    fix.type = 'button';
    fix.className = 'text-action';
    fix.textContent = guess;
    fix.dataset.gcFixEmail = guess;
    emailMsg.appendChild(fix);
    emailMsg.appendChild(document.createTextNode('?'));
  }

  /* ---- the message the APP receives ----
     Not the same string as the one in the box, and deliberately. The app's
     card has no From line of its own - Vify never collected one, which is why
     every historical message on this store ends in a hand-typed "Love, Lize"
     - so a card written in our form would reach its recipient anonymous. The
     sign-off is appended for the app only; our own From property keeps it as
     a field, so the order stays structured for us and the card stays warm for
     them.

     Skipped when the name is already somewhere in the message, because most
     people still sign off by hand and "- Nico" under "Love, Nico" reads as a
     glitch. */
  function outgoingMessage() {
    const msg = msgIn.value.trim();
    const from = fromIn.value.trim();
    if (!from) return msg;
    if (msg && msg.toLowerCase().indexOf(from.toLowerCase()) !== -1) return msg;
    return (msg ? msg + '\n\n' : '') + '- ' + from;
  }

  /* ---- JENNIE WRITES THE MESSAGE ----
     The one field on this page people actually get stuck on, answered by an
     endpoint that already existed and that nothing in this theme had ever
     called: GET /api/client/compose?kind=gift. That lane is typed on purpose -
     it assembles its own facts and lets the model do nothing but phrase them,
     and the two names a shopper types are stripped to letters and spaces
     server-side before the model sees them, so a name cannot carry an
     instruction. It caches on the facts, and a browser over its budget gets a
     composed line rather than a 429.

     NO ENDPOINT, NO BUTTON. The block ships hidden and is revealed here only
     when there is somewhere to send the request - the same rule the account
     preferences block follows. A control that cannot work should not be on
     the page.

     IT SUGGESTS. IT NEVER OVERWRITES. The line lands UNDER the box with a
     button to take it, exactly the way the email typo guard above offers a
     correction, so pressing this out of curiosity cannot cost somebody the
     three sentences they had already written. */
  const composeBox = q('[data-gc-compose]');
  const composeOut = q('[data-gc-compose-out]');
  const composeNote = q('[data-gc-compose-note]');
  const briefIn = q('[data-gc-brief]');
  const improveBtn = q('[data-gc-improve]');
  const EP = ((window.theme && window.theme.jennie && window.theme.jennie.endpoint) || '')
    .trim().replace(/\/+$/, '');
  let occasion = '';
  let variant = 1;
  let suggestion = '';
  let writing = false;
  let lastMode = 'write';
  if (composeBox && EP) composeBox.hidden = false;

  /* IMPROVE IS ONLY OFFERED WHEN THERE IS SOMETHING TO IMPROVE. A button
     promising to better an empty box is a button that writes from scratch
     wearing the wrong label. Twelve characters, because "Happy birthday" from
     a tapped opener is a message and "Hi" is a false start. */
  function paintImprove() {
    if (!improveBtn) return;
    improveBtn.hidden = msgIn.value.trim().length < 12;
  }

  /* `mode` is 'write' or 'improve' and it decides ONE thing: whether the
     shopper's own text goes down the wire as `draft`. The endpoint reads that
     as "edit this", so passing it on a blank box would be asking the model to
     improve nothing. */
  function write(next, mode) {
    if (!composeBox || !EP || writing) return;
    /* Five is the endpoint's own ceiling on `variant`; wrapping rather than
       clamping means "another go" keeps giving a different line forever
       instead of silently returning the same one on the sixth press. */
    variant = ((Math.max(1, next) - 1) % 5) + 1;
    lastMode = mode === 'improve' ? 'improve' : 'write';
    writing = true;
    if (composeOut) composeOut.hidden = false;
    if (composeNote) {
      composeNote.textContent = lastMode === 'improve'
        ? 'Jennie is reading yours...' : 'Jennie is writing...';
      composeNote.classList.add('is-working');
    }
    /* The brief is what they told THIS page they want said; the draft is what
       is in the box, and it only rides along on Improve. Both are bounded here
       as well as server-side - words() is the guard, this is just not sending
       a kilobyte up a query string. */
    const brief = briefIn ? briefIn.value.trim().slice(0, 200) : '';
    const draft = lastMode === 'improve' ? msgIn.value.trim().slice(0, 400) : '';
    const url = EP + '/api/client/compose?kind=gift'
      + '&to=' + encodeURIComponent(toIn.value.trim().slice(0, 40))
      + '&from=' + encodeURIComponent(fromIn.value.trim().slice(0, 40))
      + '&occasion=' + encodeURIComponent(occasion.slice(0, 40))
      + (brief ? '&brief=' + encodeURIComponent(brief) : '')
      + (draft ? '&draft=' + encodeURIComponent(draft) : '')
      + '&variant=' + variant;
    fetch(url, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const note = d && typeof d.note === 'string' ? d.note.trim() : '';
        if (!note) throw new Error('no line');
        suggestion = note;
        if (composeNote) {
          composeNote.textContent = note;
          composeNote.classList.remove('is-working');
        }
      })
      .catch(() => {
        /* The lane is off, the network is out, or the dial is down. Say so in
           one sentence and take the offer away rather than leaving a button
           that does nothing - the openers above still work. */
        suggestion = '';
        if (composeOut) composeOut.hidden = true;
        if (composeBox) {
          composeBox.textContent = '';
          const p = document.createElement('p');
          p.className = 'gc-compose__ask msg';
          p.textContent = 'Jennie cannot help with the words just now. The openers above still work.';
          composeBox.appendChild(p);
        }
      })
      .then(() => { writing = false; });
  }

  /* ---- 3 · the properties ----
     Target phase. See the head note: this MUST land before the event reaches
     initCart on document. */
  form.addEventListener('submit', () => {
    const design = root.querySelector('[data-gc-design]:checked');
    const image = design ? design.value : (art ? (art.currentSrc || art.getAttribute('src') || '') : '');
    const later = whenNow() === 'later';
    const stamp = later && dateIn && dateIn.value
      ? dateIn.value + 'T' + ((timeIn && timeIn.value) || '08:00')
      : zoneStamp(new Date());

    if (payload) {
      payload.value = JSON.stringify({
        recipient_name: toIn.value.trim(),
        recipient_email: emailIn.value.trim(),
        message: outgoingMessage(),
        gift_card_image_selected: image,
        locale: (document.documentElement.lang || 'en').slice(0, 2),
        product_id: PRODUCT_ID,
        country: '',
        firstName: '',
        last_name: '',
        productType: 'gift_card',
        address: '',
        deliveryDate: stamp,
        timezone: ZONE || 'Africa/Johannesburg',
        zipCode: '',
        city: '',
        is_physical_gift_card: false
      });
      payload.disabled = false;
    }
    if (imageIn) { imageIn.value = image; imageIn.disabled = false; }
    root.querySelectorAll('[data-gc-postal]').forEach((el) => { el.disabled = false; });
    if (sendLabel) {
      const z = zoneShort();
      sendLabel.value = later && dateIn && dateIn.value
        ? humanDate(dateIn.value, timeIn ? timeIn.value : '') + (z ? ' ' + z : '')
        : 'As soon as the order goes through';
      sendLabel.disabled = false;
    }
  });

  /* ---- wiring ---- */
  form.addEventListener('input', (e) => {
    if (e.target === emailIn) { autoMine = false; checkEmail(); }
    paint(); paintCount(); paintReview(); paintImprove();
  });
  form.addEventListener('change', (e) => {
    if (e.target.hasAttribute('data-gc-mode')) paintMode();
    if (e.target.hasAttribute('data-gc-when')) paintWhen();
    paint(); paintReview();
  });

  root.addEventListener('click', (e) => {
    const fix = e.target.closest('[data-gc-fix-email]');
    if (fix) {
      emailIn.value = fix.dataset.gcFixEmail;
      autoMine = false;
      checkEmail(); paintReview(); emailIn.focus();
      return;
    }
    const starter = e.target.closest('[data-gc-starter]');
    if (starter) {
      const text = starter.dataset.gcStarter || '';
      const cur = msgIn.value;
      /* An OPENER, not a replacement: it goes in front of whatever is already
         written, so tapping one and then typing is the natural order. */
      if (cur.trim().toLowerCase().indexOf(text.toLowerCase()) !== 0) {
        msgIn.value = (text + '\n\n' + cur).slice(0, MAX);
      }
      /* Remembered as the OCCASION for Jennie, because it is the only thing on
         this page that says what the card is for. */
      occasion = text;
      msgIn.focus();
      msgIn.setSelectionRange(msgIn.value.length, msgIn.value.length);
      paint(); paintCount(); paintReview();
      return;
    }
    if (e.target.closest('[data-gc-write]')) { write(1, 'write'); return; }
    if (e.target.closest('[data-gc-improve]')) { write(1, 'improve'); return; }
    /* Another go keeps the job it was doing - pressing it after Improve must
       not quietly switch to writing a new message from scratch. */
    if (e.target.closest('[data-gc-again]')) { write(variant + 1, lastMode); return; }
    if (e.target.closest('[data-gc-use]')) {
      if (!suggestion) return;
      msgIn.value = suggestion.slice(0, MAX);
      if (composeOut) composeOut.hidden = true;
      msgIn.focus();
      msgIn.setSelectionRange(msgIn.value.length, msgIn.value.length);
      paint(); paintCount(); paintReview();
      return;
    }
    if (e.target.closest('[data-gc-close]') && modal) { modal.close(); return; }
    if (e.target.closest('[data-gc-open-preview]') && modal && card) {
      const slot = q('[data-gc-preview-slot]');
      const note = q('[data-gc-preview-note]');
      paint();
      if (slot) {
        const copy = card.cloneNode(true);
        /* The clone must not answer to any of the hooks the original owns -
           it is a photograph of the card, not a second card. */
        copy.removeAttribute('data-gc-card');
        copy.querySelectorAll('[data-add-art]').forEach((n) => n.removeAttribute('data-add-art'));
        slot.textContent = '';
        slot.appendChild(copy);
      }
      if (note) note.textContent = review ? review.textContent : '';
      if (typeof modal.showModal === 'function' && !modal.open) modal.showModal();
    }
  });
  if (modal) {
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.close(); });
  }

  /* ---- WHAT IS LEFT ON IT ----
     The recipient's half of this page. Everything above is written for the
     buyer; somebody arriving months later with a code wants one number, and
     until this existed the only way to get it was to email support.

     POST /api/giftcard/balance is what answers, and nothing in this theme had
     ever called it - the endpoint was unreachable as well as broken. The
     balance comes from Shopify's own ledger at read time; JENNIE only knows
     which card the code is.

     THE FIELD IS FORGIVING BECAUSE PEOPLE ARE NOT PRINTERS. Shopify prints a
     code in four-character groups and a person retypes it with whatever
     spacing and case they like. The server folds both out on both sides now
     (that was the bug: it upper-cased the input and compared it to a
     lower-case column, so every real lookup answered "no such card"), and the
     field here does not fight them either.

     NO ENDPOINT, NO BLOCK. Revealed only when there is somewhere to ask, the
     same rule the composer above follows. */
  const bal = q('[data-gc-balance]');
  const balForm = q('[data-gc-balance-form]');
  const balCode = q('[data-gc-balance-code]');
  const balGo = q('[data-gc-balance-go]');
  const balOut = q('[data-gc-balance-out]');
  if (bal && balForm && balCode && EP) bal.hidden = false;

  /* One <strong>, around the number and nothing else, because the number is
     the answer and the rest of the line is only scaffolding around it. Built
     as nodes rather than innerHTML: the head is ours, but `tail` can carry an
     expiry date straight off the API, and this is the one block on the page a
     stranger's typing reaches. */
  function balSay(head, strong, tail, tone) {
    if (!balOut) return;
    balOut.textContent = '';
    balOut.classList.remove('is-good', 'is-warn');
    if (tone) balOut.classList.add(tone);
    if (head) balOut.appendChild(document.createTextNode(head));
    if (strong) {
      const b = document.createElement('strong');
      b.textContent = strong;
      balOut.appendChild(b);
    }
    if (tail) balOut.appendChild(document.createTextNode(tail));
  }

  let checking = false;
  if (balForm) balForm.addEventListener('submit', (e) => {
    e.preventDefault();
    if (checking || !EP) return;
    /* Folded here too, so somebody who typed three characters is told so
       rather than sent to an endpoint that would rightly tell them nothing.
       The server folds it again on its own side - this is a courtesy, not the
       guard. */
    const code = balCode.value.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (code.length < 8) {
      balSay('That does not look like a full code. It is the long one at the bottom of the email.', '', '', 'is-warn');
      balCode.focus();
      return;
    }
    checking = true;
    if (balGo) balGo.disabled = true;
    balSay('Checking...', '', '', '');
    fetch(EP + '/api/giftcard/balance', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ code: code })
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) throw new Error('no answer');
        if (d.unreachable) {
          /* "We could not ask" is not "no such card". Telling somebody holding
             a perfectly good card that it does not exist is much the worse of
             the two wrong answers, so the two are never collapsed. */
          balSay('We cannot check balances this minute. Give it a moment and try again.', '', '', 'is-warn');
          return;
        }
        if (!d.found) {
          balSay('We cannot find that code. Check it against the email it came in, and mind the letter O against the number 0.', '', '', 'is-warn');
          return;
        }
        if (d.enabled === false) {
          balSay('That card has been switched off. Reply to the email it came in and we will sort it out.', '', '', 'is-warn');
          return;
        }
        /* bagMoney, not a second formatter. Its own note says only that
           way: two surfaces printing the same number differently reads as a
           bug even when both are right. */
        const left = bagMoney(d.balanceCents);
        const full = bagMoney(d.initialCents);
        const runs = d.expiresOn ? ' It runs until ' + d.expiresOn + '.' : '';
        /* A SPENT CARD IS NOT GOOD NEWS AND MUST NOT BE DRESSED AS IT. The
           found branch was one line, so an emptied card answered "R 0 left of
           R 1000" in the same green as a full one - technically true, and
           exactly the tone somebody does not want when the answer is nothing.
           Zero gets its own sentence and no tone at all. */
        if (!d.balanceCents) {
          balSay('There is nothing left on that one. It started at ', full, '.', '');
          return;
        }
        const tail = (left === full ? ', all of it still there.' : ' left of ' + full + '.') + runs;
        balSay('', left, tail, 'is-good');
      })
      .catch(() => {
        balSay('We cannot check balances this minute. Give it a moment and try again.', '', '', 'is-warn');
      })
      .then(() => {
        checking = false;
        if (balGo) balGo.disabled = false;
      });
  });

  paintMode(); paintWhen(); paint(); paintCount(); paintReview();
}
